/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";

// Material UI table
import { MaterialReactTable } from "material-react-table";
//Material UI components for rendring menu and others
import { MenuItem } from "@mui/material";

import ProjectManagementCommonHeader from "components/ProjectManagementComponents/Common/Header/ProjectManagementCommonHeader";

import ChallengesCommonHeader from "components/ChallengeComponents/Common/Header/ChallengesCommonHeader";
import ReportListViewHeader from "./Header/ReportListViewHeader";

//import { assetImages } from "constants";

import {
  createdAtToDateConverter,
  getDateFormatInSpanish,
} from "helper/Common/CommonHelper";

/*import url and gateway methods */
import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import { useTranslation } from "react-i18next";
import { assetImages } from "constants";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";

/**
 * ReportListViewBody Component
 *
 * This component displays a list of challenge responses in a tabular format.
 * It fetches data from the backend, manages table state (like row selection),
 * and renders the data using MaterialReactTable. It also handles access control
 * based on user roles and module access.
 *
 * @returns {JSX.Element} The rendered Report List View body.
 */
const ReportListViewBody = () => {
  const params = useParams();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const moduleAccess = localStorage.getItem("moduleaccess");
  const { t, i18n } = useTranslation(); // for translation

  // State to manage row selection in the MaterialReactTable.
  const [rowSelection, setRowSelection] = useState({});
  // State to store IDs of selected responses.
  const [selectedResponseIds, setSelectedResponseIds] = useState([]);

  // State to hold the list of challenge responses fetched from the API.
  const [responseList, setResponseList] = useState([]);
  // State to indicate if data is currently being loaded from the API.
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Fetches all challenge responses from the backend API.
   *
   * This asynchronous function makes an API call to retrieve the full report
   * of challenge responses. It updates the loading state, resets row selections,
   * and populates the `responseList` state upon successful data retrieval.
   *
   * @returns {Promise<void>} A promise that resolves when the data fetching is complete.
   */
  const getAllChallengeResponse = async () => {
    setRowSelection({}); // Reset row selection
    setSelectedResponseIds([]); // Clear selected response IDs

    try {
      setIsLoading(true); // Set loading state to true
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_CHALLENGE_FULL_REPORT +
        ``;

      console.log("response requestUrl", requestUrl);

      const response = await getData(requestUrl); // Make API call

      console.log("response data in getallchallengeresponses >>> ", response);

      if (response.status) {
        setResponseList(response.data); // Update response list with fetched data
      }

      setIsLoading(false); // Set loading state to false
    } catch (error) {
      console.log(error.message); // Log any errors during the API call
    }
  };

  /**
   * Defines the columns for the MaterialReactTable.
   *
   * This memoized array of column definitions specifies how each column in the table
   * should be rendered, including accessor keys, headers, and custom cell rendering
   * for specific columns like 'Response Code', 'Challenge', 'Provider / Applicant',
   * 'Submitted By', 'Responded on', 'P. Budget', 'Cr Budget', 'skill / points / exp. years',
   * and 'Agency'.
   *
   * @returns {Array<Object>} An array of column definitions for MaterialReactTable.
   */
  const columns = useMemo(
    () => [
      {
        accessorKey: "responsecode",
        header: "Response Code",
        Cell: ({ row }) => (
          <div className="title border-bottom-0">
            <span
              className={`fw-semibold ${
                row.original.approvalstatus == "2"
                  ? "text-danger"
                  : row.original.approvalstatus == "1"
                  ? "text-success"
                  : "text-black"
              }`}
            >
              {row.original.responsecode}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "jobcode",
        header: "Job Code",
      },
      {
        accessorKey: "challengename",
        header: "Challenge",
        Cell: ({ row }) => (
          <div className="title border-bottom-0" style={{ width: "200px" }}>
            <a
              target="_blank"
              href={`${url.FRONTEND_BASE_URL}/jobs/details/${row.original.challengeid}`}
              className="d-flex flex-auto"
            >
              <span className="d-block material-symbols-outlined icon-md me-2">
                link
              </span>
              {row.original.challengename.slice(0, 50)}
            </a>
          </div>
        ),
      },
      {
        accessorKey: "leadname",
        header: "Lead",
      },
      {
        accessorKey: "applicantname",
        header: "Provider / Applicant",
        Cell: ({ row }) => (
          <div className="title border-bottom-0">
            <div className="profile d-flex align-items-center gap-2">
              <div>
                <p className="fs-md fw-semibold mb-0">
                  {row.original.applicantname}
                </p>
                <Link to="#" className="d-block lh-1">
                  {row.original.applicantemail}
                </Link>
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "submittedbyname",
        header: "Submitted By",
        Cell: ({ row }) => (
          <div className="title border-bottom-0">
            <div className="profile d-flex align-items-center gap-2">
              <div>
                <p className="fs-md fw-semibold mb-0">
                  {row.original.submittedbyname}
                </p>
                <Link to="#" className="d-block lh-1">
                  {row.original.submittedbyemail}
                </Link>
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "challengedate",
        header: "Challenge Date",
      },
      {
        accessorKey: "applieddate",
        header: "Applied On",
      },
      {
        accessorKey: "approvalresponsedate",
        header: "Responded on",
        Cell: ({ row }) => (
          <div
            className={`title border-bottom-0 ${
              row.original.approvalstatus == "2"
                ? "text-danger"
                : row.original.approvalstatus == "1"
                ? "text-success"
                : "text-black"
            }`}
          >
            {row.original.approvalresponsedate === ""
              ? "-"
              : row.original.approvalresponsedate}
          </div>
        ),
      },
      {
        accessorKey: "providerbudget",
        header: "P. Budget",
        Cell: ({ row }) => (
          <div className="title border-bottom-0">
            {row.original.providerbudget ? row.original.providerbudget : "-"}{" "}
            &euro;
          </div>
        ),
      },
      {
        accessorKey: "customerbudget",
        header: "Cr Budget",
        Cell: ({ row }) => (
          <div className="title border-bottom-0">
            {row.original.customerbudget ? row.original.customerbudget : "-"}{" "}
            &euro;
          </div>
        ),
      },
      {
        accessorKey: "candidateskillpoints",
        header: "skill / points (N/10) / exp. years",
        Cell: ({ row }) => (
          <div className="title border-bottom-0" style={{ width: "300px" }}>
            {row.original.candidateskillpoints.map((skillpoint, index) => {
              return (
                <p key={index}>
                  {skillpoint.skillname ? skillpoint.skillname : "--"} /{" "}
                  {skillpoint.points} pnts / {skillpoint.experienceyear} yrs
                </p>
              );
            })}
          </div>
        ),
      },
      {
        accessorKey: "isagency",
        header: "Agency",
        Cell: ({ row }) => (
          <div className="title border-bottom-0">
            {row.original.isagency ? row.original.submittedbyname : "-"}
          </div>
        ),
      },
      {
        accessorKey: "responsecount",
        header: "Response Count",
      },
    ],
    []
  );

  /**
   * useEffect hook to fetch all challenge responses when the component mounts.
   * The empty dependency array ensures this effect runs only once after the initial render.
   */
  useEffect(() => {
    getAllChallengeResponse();
  }, []);

  // Conditional rendering based on user roles and module access.
  // Only users with 'ADMIN', 'SUPER_ADMIN' roles or 'MOD_CHALLENGE' access can view the report.
  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    moduleAccess.includes("MOD_CHALLENGE")
  ) {
    return (
      <div id="content_wrapper">
        <section className="survey-wrapper bg-white pb-5">
          <ChallengesCommonHeader componentName="fullreport" />

          <div className="container-fluid px-lg-5">
            {/* Header component for the report list view, providing refresh functionality */}
            <ReportListViewHeader refreshList={getAllChallengeResponse} />

            {/* Conditional rendering: show LoadingSkeleton if data is loading, otherwise show the table */}
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns} // Column definitions for the table
                  data={responseList} // Data to be displayed in the table
                  enableGrouping // Enable row grouping functionality
                  enableRowSelection // Enable checkboxes for row selection
                  getRowId={(row) => row._id} // Function to get a unique ID for each row
                  onRowSelectionChange={setRowSelection} // Callback to update row selection state
                  state={{ rowSelection }} // Pass the managed row selection state to the table
                  enableRowActions // Enable the action column for each row
                  renderRowActionMenuItems={({ row }) => [
                    // Menu items for row actions
                    <MenuItem key="details">
                      <Link
                        className="dropdown-item"
                        target="_blank"
                        to={`/admin/challengeresponse/details/${row.original.challengeid}/${row.id}`}
                      >
                        View Details
                      </Link>
                    </MenuItem>,
                    <MenuItem key="feedback">
                      <Link
                        className="dropdown-item"
                        target="_blank"
                        to={`/admin/customerfeedback/${row.id}`}
                      >
                        Customer Feedback
                      </Link>
                    </MenuItem>,
                  ]}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh", // Set maximum height for the table container
                    },
                  }}
                  enableStickyHeader // Enable sticky header for the table
                />
              </div>
            )}
          </div>
        </section>
      </div>
    );
  } else {
    // Render AccessDeniedView if the user does not have the required permissions.
    return <AccessDeniedView />;
  }
};

export default ReportListViewBody;
